import { Dispatch, FC, SetStateAction, useCallback, useMemo, useRef } from 'react';
import { Field, Form, Formik, FormikProps } from 'formik';
import { makeStyles } from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';

import ButtonDefault from 'shared/components/Buttons/ButtonsDefault';
import DatePickerField from '../DateRangePicker/DatePickerField';
import FiltersContainer from 'components/Organization/Filters/FiltersContainer';
import SmartGroupList from './SmartGroupList';
import LocationType from './LocationType';
import CreatedBy from './CreatedBy';

import { DashboardFiltersType } from '../index';

import { DateRangeValue, InitialFocusedRange } from '../DateRangePicker';

import styles from './StatisticsFilter.module.scss';

const useStyles = makeStyles({
	paper: {
		height: 'calc(100vh - 100px)',
		top: 'unset',
		borderLeft: 'unset',
		boxShadow: '-2px 3px 4px rgba(33, 75, 165, 0.1)',
		bottom: 0,
	},
});

type StatisticsFilterProps = {
	isFiltersOpen: boolean;
	setFiltersOpen: Dispatch<SetStateAction<boolean>>;
	filters: DashboardFiltersType;
	setFilters: Dispatch<SetStateAction<DashboardFiltersType>>;
	isSchool: boolean;
};

const StatisticsFilter: FC<StatisticsFilterProps> = ({
	isFiltersOpen,
	isSchool,
	setFiltersOpen,
	filters,
	setFilters,
}) => {
	const classes = useStyles();
	const formikRef = useRef<FormikProps<DashboardFiltersType>>(null);

	const handleSubmit = useCallback((values: DashboardFiltersType) => {
		setFilters(values);
	}, []);

	const handleReset = () => {
		formikRef.current?.resetForm();

		setFilters({
			organizationIds: [],
			fromDate: undefined,
			toDate: undefined,
			createdBy: [],
			opportunityLocationType: [],
		});
	};

	const externalRange = useMemo<DateRangeValue>(
		() => ({ startDate: filters.fromDate, endDate: filters.toDate }),
		[filters]
	);

	return (
		<Drawer
			anchor="right"
			variant="persistent"
			classes={{
				paper: classes.paper,
			}}
			open={isFiltersOpen}
		>
			<FiltersContainer
				resetForm={handleReset}
				isCloseFilters={true}
				changeVisibilityFilters={() => setFiltersOpen(false)}
				isNewDashboard={true}
			>
				<Formik innerRef={formikRef} initialValues={filters} onSubmit={handleSubmit} enableReinitialize={true}>
					{({ isValid }) => (
						<Form className={styles.statisticsFilterForm}>
							<Field name="organizationIds" as={SmartGroupList} />
							{isSchool ? (
								<Field name="opportunityLocationType" isSchool={isSchool} as={LocationType} />
							) : null}
							<Field name="createdBy" isSchool={isSchool} as={CreatedBy} />

							<Field
								name="fromDate"
								title="From:"
								isSchool={isSchool}
								externalRange={externalRange}
								initialFocusedRange={InitialFocusedRange.StartDate}
								as={DatePickerField}
							/>
							<Field
								name="toDate"
								title="To:"
								isSchool={isSchool}
								externalRange={externalRange}
								initialFocusedRange={InitialFocusedRange.EndDate}
								as={DatePickerField}
							/>

							<div className={styles.statisticsFilterForm_actions}>
								<ButtonDefault
									disabled={!isValid}
									title="Apply"
									submitType={true}
									classList="secondary md"
								/>
							</div>
						</Form>
					)}
				</Formik>
			</FiltersContainer>
		</Drawer>
	);
};

export default StatisticsFilter;
